// src/components/ClientContract.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

interface ContractSection {
    label: string;
    items: ContractItem[];
}

interface ContractItem {
    type: 'text' | 'field';
    label?: string;
    content?: string;
    value?: string;
}

const ClientContract: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [contract, setContract] = useState<any>(null);
    const [sections, setSections] = useState<ContractSection[]>([]);
    const sigCanvas = useRef<SignatureCanvas>(null);

    useEffect(() => {
        const token = localStorage.getItem('clientToken');
        console.log('ClientContract: Checking clientToken:', token);
        if (!token) {
            console.log('ClientContract: No clientToken found, redirecting to /client/login');
            navigate('/client/login');
        } else {
            fetchContract(token);
        }
    }, [id, navigate]);

    const fetchContract = async (token: string) => {
        try {
            console.log('ClientContract: Fetching contract with ID:', id);
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/ClientContracts/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setContract(response.data);
            const parsedData = JSON.parse(response.data.jsonData || '{"sections":[]}');
            setSections(parsedData.sections || []);
        } catch (error: any) {
            console.error('ClientContract: Error fetching contract:', error);
            if (error.response?.status === 401) {
                console.log('ClientContract: Unauthorized, redirecting to /client/login');
                localStorage.removeItem('clientToken');
                window.dispatchEvent(new Event('clientAuthChange'));
                navigate('/client/login');
            } else {
                alert('Failed to load contract.');
                navigate('/client/contracts');
            }
        }
    };

    const signContract = async () => {
        const signature = sigCanvas.current?.toDataURL();
        if (!signature) return alert('Please provide a signature.');
        const token = localStorage.getItem('clientToken');
        try {
            await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/ClientContracts/${id}/sign`,
                { signature },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setContract({ ...contract, status: 'signed', signature });
            alert('Contract signed successfully!');
        } catch (error: any) {
            console.error('ClientContract: Error signing contract:', error);
            if (error.response?.status === 401) {
                console.log('ClientContract: Unauthorized during signing, redirecting to /client/login');
                localStorage.removeItem('clientToken');
                window.dispatchEvent(new Event('clientAuthChange'));
                navigate('/client/login');
            } else {
                alert('Failed to sign contract. Please try again.');
            }
        }
    };

    const clearSignature = () => sigCanvas.current?.clear();

    if (!contract) return <div className="container mt-5"><div className="alert alert-info">Loading...</div></div>;

    return (
        <div className="container mt-5">
            <h2 className="mb-4 text-center">Contract: {contract.name || `Contract ${id}`}</h2>
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <p><strong>Status:</strong> {contract.status}</p>
                    {contract.status === 'signed' && (
                        <div className="alert alert-success text-center">Contract signed! Thank you.</div>
                    )}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-primary text-white">
                            <h4 className="mb-0">Contract Content</h4>
                        </div>
                        <div className="card-body">
                            {sections.length === 0 ? (
                                <p className="text-muted">No sections defined in this contract.</p>
                            ) : (
                                sections.map((section, index) => (
                                    <div key={index} className="mb-4">
                                        <h5 className="text-primary">{section.label}</h5>
                                        <div className="border rounded p-3 bg-light">
                                            {section.items.map((item, i) => (
                                                <div key={i} className="mb-2">
                                                    {item.type === 'text' && <p className="mb-0">{item.content}</p>}
                                                    {item.type === 'field' && (
                                                        <p className="mb-0">
                                                            <strong>{item.label}:</strong>{' '}
                                                            {item.value || <span className="text-muted">Not filled</span>}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    {contract.status !== 'signed' && (
                        <div className="mt-4">
                            <h5>Signature</h5>
                            <SignatureCanvas
                                ref={sigCanvas}
                                penColor="black"
                                canvasProps={{ width: 500, height: 200, className: 'border' } as React.HTMLAttributes<HTMLCanvasElement>}
                            />
                            <div className="mt-2">
                                <button onClick={clearSignature} className="btn btn-outline-secondary me-2">
                                    Clear
                                </button>
                                <button onClick={signContract} className="btn btn-success">
                                    Sign Contract
                                </button>
                            </div>
                        </div>
                    )}
                    {contract.signature && (
                        <div className="mt-4">
                            <h5>Signed:</h5>
                            <img
                                src={contract.signature}
                                alt="Signature"
                                className="border"
                                style={{ maxWidth: '500px' }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientContract;